import { useCallback, useEffect, useMemo, useRef, useState, type ReactNode } from 'react';
import { apiRequest, setAccessToken } from '@/lib/api/client';
import { tokenStorage } from '@/lib/api/tokenStorage';
import { AuthContext, type AuthState, type CurrentUser } from './useAuth';

interface Session {
  accessToken: string;
  expiresAt: string;
  refreshToken: string;
  productRole: string;
}

/**
 * Session state for the whole application.
 *
 * @remarks
 * The claims in the access token are never used to decide anything. They exist to drive
 * what the interface offers - which view to land on, which buttons to render - and the
 * server re-decides every request regardless. A client that treats a role claim as
 * authorization is a client that can be lied to by editing memory
 * (`frontend-standards.md` section 5).
 */
export function AuthProvider({ children }: { children: ReactNode }) {
  const [status, setStatus] = useState<AuthState['status']>('restoring');
  const [user, setUser] = useState<CurrentUser | null>(null);

  const loadUser = useCallback(async () => {
    const profile = await apiRequest<CurrentUser>('/auth/me');
    setUser(profile);
    setStatus('signedIn');
  }, []);

  /**
   * Restores a session on load.
   *
   * The access token lives in memory only, so a page refresh always starts without one.
   * The stored refresh token is exchanged for a fresh pair; failure is an ordinary outcome
   * - the token expired, or was revoked - and means signed out rather than broken.
   */
  // React 18 mounts effects twice in development StrictMode, and a refresh token is
  // single-use: the server rotates it on first presentation and treats a second presentation
  // of a rotated token as theft, revoking the whole family. So without this the second run
  // logged the developer out - or handed them an access token whose family was already dead -
  // on every page load. AuthCallbackPage carries the same guard, for the same reason.
  const restoring = useRef(false);

  useEffect(() => {
    if (restoring.current) {
      return;
    }

    restoring.current = true;

    void (async () => {
      const refreshToken = tokenStorage.get();

      if (!refreshToken) {
        setStatus('signedOut');
        return;
      }

      try {
        const session = await apiRequest<Session>('/auth/session/refresh', {
          method: 'POST',
          body: { refreshToken },
          anonymous: true,
        });

        setAccessToken(session.accessToken);
        tokenStorage.set(session.refreshToken);

        await loadUser();
      } catch {
        tokenStorage.clear();
        setAccessToken(null);
        setStatus('signedOut');
      }
    })();

    // There is deliberately no cleanup that discards the result.
    //
    // This effect used to set a `cancelled` flag on teardown so a late response could not
    // write state after unmount. With the guard above that becomes harmful rather than
    // careful: StrictMode tears the first run down before starting the second, so the flag
    // cancelled the only run the guard allows and the application sat in "restoring" for
    // ever. The first version of this fix did exactly that, and the test below caught it.
    //
    // Nothing is lost by dropping it. React 18 makes a state update on an unmounted component
    // a no-op rather than a warning, and this provider is the application root - if it has
    // unmounted, there is no application left to mislead.
  }, [loadUser]);

  /** Trades the one-time handoff ticket from the OAuth redirect for a session. */
  const completeSignIn = useCallback(
    async (handoffTicket: string) => {
      const session = await apiRequest<Session>('/auth/session', {
        method: 'POST',
        body: { handoffTicket },
        anonymous: true,
      });

      setAccessToken(session.accessToken);
      tokenStorage.set(session.refreshToken);

      await loadUser();
    },
    [loadUser],
  );

  const signOut = useCallback(async () => {
    const refreshToken = tokenStorage.get();

    // Local state is cleared first and unconditionally. If the server call fails, the user
    // must still end up signed out here - leaving them apparently signed in because the
    // network blipped is the wrong direction to fail.
    tokenStorage.clear();
    setAccessToken(null);
    setUser(null);
    setStatus('signedOut');

    if (refreshToken) {
      try {
        await apiRequest('/auth/session/sign-out', {
          method: 'POST',
          body: { refreshToken },
          anonymous: true,
        });
      } catch {
        // The server-side family revocation did not happen. The token still expires on its
        // own, and there is nothing the user can do about it.
      }
    }
  }, []);

  const value = useMemo<AuthState>(
    () => ({ status, user, signOut, completeSignIn, refreshUser: loadUser }),
    [status, user, signOut, completeSignIn, loadUser],
  );

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}
